"use client";

import { useChessSocketContext } from "@/app/oyun/_providers/ChessSocketProvider";
import type { BattleInvite } from "@/components/battle-invite-item";
import useStore from "@/store/useStore";
import type {
    BattleInvitationPayload,
    GameInviteData,
} from "@satranc365/shared";
import { useEffect } from "react";

/**
 * Registers socket listeners for incoming game and battle invite notifications
 * and writes them into the global notification store.
 *
 * Must be called inside the ChessSocketProvider tree.
 * Called from NavbarNotification so its lifecycle matches the navbar mount.
 */
export function useNotificationEvents() {
    const { socket } = useChessSocketContext();
    const { addGameInvite, setGameInvites, removeGameInvite, addBattleInvite } =
        useStore();

    // Chess game invites
    useEffect(() => {
        if (!socket) return;

        const handleGameInviteReceived = (data: GameInviteData) => {
            addGameInvite(data);
        };

        const handlePendingGameInvites = (data: GameInviteData[]) => {
            setGameInvites(data);
        };

        const handleGameInviteExpired = (data: { inviteId: number }) => {
            removeGameInvite(data.inviteId);
        };

        socket.on("game_invite_received", handleGameInviteReceived);
        socket.on("pending_game_invites", handlePendingGameInvites);
        socket.on("game_invite_expired", handleGameInviteExpired);

        return () => {
            socket.off("game_invite_received", handleGameInviteReceived);
            socket.off("pending_game_invites", handlePendingGameInvites);
            socket.off("game_invite_expired", handleGameInviteExpired);
        };
    }, [socket, addGameInvite, setGameInvites, removeGameInvite]);

    // Battle invites (arrive on the chess socket as "game_invitation")
    useEffect(() => {
        if (!socket) return;

        const handleBattleInvitation = (data: BattleInvitationPayload) => {
            if (!data.battleId || !data.invitationId) return;

            const invite: BattleInvite = {
                invitationId: data.invitationId,
                battleId: data.battleId,
                fromUserId: data.fromUserId,
                fromDisplayUsername: data.fromDisplayUsername,
                toUserId: data.toUserId,
                duration: data.duration,
            };

            addBattleInvite(invite);
        };

        socket.on("game_invitation", handleBattleInvitation);

        return () => {
            socket.off("game_invitation", handleBattleInvitation);
        };
    }, [socket, addBattleInvite]);
}
